import { Box, InputBase, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { styles } from "@/pages/Settings/stylesSettings";
import LoadingSvg from "@/assets/svg/SocialMedia/LoadingSvg";
import SuccessSvg from "@/assets/svg/SocialMedia/SuccessSvg";
import ErrorSvg from "@/assets/svg/SocialMedia/ErrorSvg";
import theme from "@/theme";

type Platform = "twitter" | "facebook" | "instagram" | "linkedin";

type Props = {
  icon: React.ReactNode;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  platform: Platform;
  isFromApi?: boolean;
};

const PLACEHOLDERS: Record<Platform, string> = {
  twitter: "https://x.com/handle or https://twitter.com/handle",
  facebook: "https://www.facebook.com/yourpage",
  instagram: "https://www.instagram.com/yourname",
  linkedin: "https://www.linkedin.com/in/yourname",
};

const allowedHosts: Record<Platform, string[]> = {
  twitter: ["x.com", "twitter.com", "www.x.com", "www.twitter.com"],
  facebook: ["facebook.com", "www.facebook.com"],
  instagram: ["instagram.com", "www.instagram.com"],
  linkedin: ["linkedin.com", "www.linkedin.com"],
};

const errorTexts: Record<Platform, string> = {
  twitter: "Enter a valid Twitter/X link (x.com or twitter.com).",
  facebook: "Enter a valid Facebook link (facebook.com).",
  instagram: "Enter a valid Instagram link (instagram.com).",
  linkedin: "Enter a valid LinkedIn link (linkedin.com).",
};

function normalizeForValidation(raw: string): string | null {
  if (!raw?.trim()) return null;
  const val = raw.trim();
  const withProto = /^https?:\/\//i.test(val) ? val : `https://${val}`;
  try {
    const u = new URL(withProto);
    return u.toString();
  } catch {
    return null;
  }
}

export function validateSocialUrl(platform: Platform, raw: string): boolean {
  const normalized = normalizeForValidation(raw);
  if (!normalized) return false;

  let u: URL;
  try {
    u = new URL(normalized);
  } catch {
    return false;
  }

  const host = u.hostname.toLowerCase();
  if (!allowedHosts[platform].includes(host)) return false;
  return true;
}

const SocialMedia = ({ icon, placeholder, value, onChange, platform, isFromApi }: Props) => {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isValueFromApi, setIsValueFromApi] = useState(false);

  useEffect(() => {
    if (value && isFromApi) {
      setStatus("success");
      setIsValueFromApi(true);
    }
  }, [value, isFromApi]);

  const runValidation = () => {
    if (isValueFromApi) return;
    setStatus("loading");
    setTimeout(() => {
      const ok = validateSocialUrl(platform, value);
      setStatus(ok ? "success" : "error");
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      runValidation();
    }
  };

  useEffect(() => {
    if (!value) {
      setStatus("idle");
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      return;
    }

    if (isValueFromApi) {
      setStatus("success");
      return;
    }

    setStatus("idle");
    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      runValidation();
    }, 100);

    return () => {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
    };
  }, [value, platform, isValueFromApi]);

  return (
    <>
      <Box>
        <Box sx={styles.socialMediaWrapper}>
          {icon}
          <Typography sx={styles.socialUrlLabel}>URL</Typography>
          <Typography sx={styles.separator}>|</Typography>

          <InputBase
            placeholder={placeholder || PLACEHOLDERS[platform]}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (isValueFromApi) setIsValueFromApi(false);
            }}
            onKeyDown={handleKeyDown}
            sx={{
              ...styles.socialMediaInput,
              "& .MuiInputBase-input": {
                ...styles.socialMediaInput["& .MuiInputBase-input"],
                color: status === "success" ? theme.palette.custom.mainBlue : "#2A2D31",
                textDecoration: status === "success" ? "underline" : "none",
                textDecorationStyle: "solid",
                textDecorationColor: theme.palette.custom.mainBlue,
                textDecorationThickness: "0.5px",
                textUnderlineOffset: "2px",
              },
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {status === "loading" && <LoadingSvg />}
            {status === "success" && <SuccessSvg />}
            {status === "error" && <ErrorSvg />}
          </Box>
        </Box>
        {status === "error" && (
          <Typography sx={styles.errorText}>{errorTexts[platform]}</Typography>
        )}
      </Box>
    </>
  );
};

export default SocialMedia;
